import React, { ReactElement, ReactNode } from "react"
import reactStringReplace from "react-string-replace"

export type TParseTextColourOptions = {
  colourStyle?: React.CSSProperties
  noColourStyle?: React.CSSProperties
}

const createSpanFromMatches = (
  match: string,
  index: number,
  offset: number,
  options?: TParseTextColourOptions
): React.ReactNode => {
  // Regex which grabs the individual pieces of the string i.e. ["white", "var(--white)"]
  const matches = match.match(/\((.+)\)(?=\[((?:#\w+)|(?:var\(--(\w|-)+\)))\])/)
  return (
    <span
      key={index}
      style={{ color: `${matches[2]}`, ...options?.colourStyle }}
    >
      {matches[1]}
    </span>
  )
}

const matchRegex = (
  child: ReactNode,
  index: number = 0,
  options?: TParseTextColourOptions
) => {
  // Regex which gets entire group i.e. "(white)[var(--white)]"
  const regex = /(\(.+\)(?:(?:\[var\(.+\)\])|(?:\[#\w+\])))/g

  // If the user hasn't provided any marks (bold, italic, etc) in the rich text field then it will just be a string
  if (typeof child === "string") {
    // Since it's a string we want to apply our styling options
    // First need to check if there's a match
    const matches = child.match(regex)
    if (matches) {
      return (
        // Wrap a span around that applies to the text with no colour
        <span style={options?.noColourStyle || {}} key={index}>
          {/* Now take the matched text and apply colour to it & extra style options */}
          {reactStringReplace(
            child,
            regex,
            (match: string, index: number, offset: number) =>
              createSpanFromMatches(match, index, offset, options)
          )}
        </span>
      )
    }
  }

  // If the child has marks (bold, italic, etc) then it will be a ReactElement
  if (typeof child === "object") {
    const content = (child as ReactElement).props?.children

    /* 
      Return cloned element so we can still wrap with richtext marks

      In this case we don't wrap in span so we don't overwrite the richtext marks that have been chosen in Contentful.
    */
    return (
      <React.Fragment key={index}>
        {React.cloneElement(
          child as ReactElement,
          {},
          reactStringReplace(content, regex, createSpanFromMatches)
        )}
      </React.Fragment>
    )
  }
  return child
}

/**
 * Parses inline text colour provided the format is (white)[#fff] or (white)[var(--white)] and converts into <span> tags
 * @param children react nodes to add colour to
 * @param options options for providing extra styles for the `<span>`s
 * @returns ReactNode children with colour added as a `<span>`
 * @example
 * parseTextColour("(Hello)[#fff], (World)[var(--white)]")
 */
const parseTextColour = (
  children: ReactNode | ReactNode[] = [],
  options: TParseTextColourOptions = {}
): ReactNode | ReactNode[] => {
  return Array.isArray(children)
    ? children.flatMap((child, index) => matchRegex(child, index, options))
    : matchRegex(children)
}

export default parseTextColour
